
import { useState, useCallback, useEffect } from 'react';
import { searchService, SearchResult, SearchOptions } from '@/services/search';
import { useAuth } from '@/context/AuthContext';
import { useDebouncedQuery } from '@/hooks/useDebouncedQuery';

interface UseGlobalSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResult[];
  loading: boolean;
  error: string | null;
  searchHistory: string[];
  executeSearch: (searchQuery: string, options?: SearchOptions) => Promise<void>;
  clearResults: () => void;
  clearHistory: () => void;
}

export const useGlobalSearch = (debounceMs: number = 300): UseGlobalSearchReturn => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);

  const debouncedQuery = useDebouncedQuery(query, debounceMs);

  // Load search history on mount
  useEffect(() => {
    setSearchHistory(searchService.getSearchHistory());
  }, []);

  // Execute search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.trim()) {
      executeSearch(debouncedQuery);
    } else {
      setResults([]);
    }
  }, [debouncedQuery]);

  const executeSearch = useCallback(async (searchQuery: string, options?: SearchOptions) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const mergedOptions: SearchOptions = { ...(options || {}), userId: user?.id };
      const searchResults = await searchService.searchAll(searchQuery, mergedOptions);
      setResults(searchResults);
      
      // Add to search history if not empty query
      if (searchQuery.trim()) {
        searchService.addToSearchHistory(searchQuery.trim());
        setSearchHistory(searchService.getSearchHistory());
      }
    } catch (err) {
      console.error('Search failed:', err);
      setError('Поиск временно недоступен');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const clearResults = useCallback(() => {
    setResults([]);
    setQuery('');
    setError(null);
  }, []);

  const clearHistory = useCallback(() => {
    localStorage.removeItem('search_history');
    setSearchHistory([]);
  }, []);

  return {
    query,
    setQuery,
    results,
    loading,
    error,
    searchHistory,
    executeSearch,
    clearResults,
    clearHistory
  };
};
